import { Box, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { NotificationsActive, ArrowForward } from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import { useGetStudentsQuery } from "../../services/students.service";
import { useAppTheme } from "../../context/ThemeContext";
import { formatDate } from "../../util/helper";

const REMINDER_STATUSES = new Set([
  "visa:reminder",
  "admission:reminder",
  "registration:reminder",
  "pickup:reminder",
  "commission:pending",
]);

const STATUS_COLOR: Record<string, string> = {
  "visa:reminder": "#EAB308",
  "admission:reminder": "#F59E0B",
  "registration:reminder": "#0EA5E9",
  "pickup:reminder": "#8B5CF6",
  "commission:pending": "#059669",
};

function RemindersWidget() {
  const { data: students } = useGetStudentsQuery("Students");
  const navigate = useNavigate();
  const { isNewTheme, accentColor } = useAppTheme();

  const reminders = (students ?? [])
    .filter((s) => REMINDER_STATUSES.has(s.status))
    .sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return aTime - bTime; // oldest first = most overdue
    });

  return (
    <Card
      sx={
        isNewTheme
          ? {
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              display: "flex",
              flexDirection: "column",
              transition: "box-shadow 0.15s ease",
              "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.07)" },
            }
          : {
              width: "100%",
              height: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
            }
      }
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isNewTheme && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: `${accentColor}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NotificationsActive sx={{ fontSize: 18, color: accentColor }} />
            </Box>
          )}
          <Typography variant="h6" fontWeight={isNewTheme ? 700 : 600} color={isNewTheme ? "text.primary" : "black"}>
            Reminders
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={reminders.length}
            size="small"
            color={reminders.length > 0 ? "warning" : "default"}
            sx={{ fontWeight: 700, borderRadius: "4px", minWidth: 28 }}
          />
          <Link to="/students" style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.3,
                color: "primary.main",
                fontSize: "0.75rem",
                fontWeight: 600,
                opacity: 0.85,
                "&:hover": { opacity: 1 },
              }}
            >
              View all
              <ArrowForward sx={{ fontSize: 13 }} />
            </Box>
          </Link>
        </Stack>
      </Box>

      <Divider sx={isNewTheme ? { borderColor: "#E2E8F0" } : {}} />

      <CardContent sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        {reminders.length === 0 ? (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ textAlign: "center", mt: 3, fontStyle: "italic" }}
          >
            No pending reminders
          </Typography>
        ) : (
          <Stack
            direction="column"
            divider={<Divider sx={isNewTheme ? { borderColor: "#F1F5F9" } : {}} />}
          >
            {reminders.slice(0, 8).map((s) => {
              const color = STATUS_COLOR[s.status] ?? "#64748B";
              return (
                <Tooltip key={s._id} title={`Click to view ${s.fullName}'s profile`} placement="left" arrow>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    p={1}
                    onClick={() => navigate(`/students/${s._id}`)}
                    sx={
                      isNewTheme
                        ? {
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background 0.15s ease",
                            "&:hover": { bgcolor: "#F8FAFC" },
                          }
                        : { cursor: "pointer" }
                    }
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={isNewTheme ? 600 : 400}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {s.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.62rem" }}>
                        since {formatDate(s.updatedAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={s.status.replace(":", " ")}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        borderRadius: "4px",
                        fontSize: "0.6rem",
                        height: 20,
                        flexShrink: 0,
                        bgcolor: color + "20",
                        color: color,
                        border: `1px solid ${color}40`,
                      }}
                    />
                  </Stack>
                </Tooltip>
              );
            })}
            {reminders.length > 8 && (
              <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center", pt: 1 }}>
                +{reminders.length - 8} more
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default RemindersWidget;
